import { useState, useEffect, lazy, Suspense } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import HeroSection from "@/components/HeroSection";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle2, Ticket, ArrowRight, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

const Navbar = lazy(() => import("@/components/Navbar"));
const Categories = lazy(() => import("@/components/Categories"));
const FeaturedSections = lazy(() => import("@/components/FeaturedSection"));
const HomeSections = lazy(() => import("@/components/HomeSections"));
const PopularActivities = lazy(() => import("@/components/PopularActivities"));
const BestSellers = lazy(() => import("@/components/BestSellers"));
const GroupTicketBanner = lazy(() => import("@/components/GroupTicketBanner"));
const Footer = lazy(() => import("@/components/Footer"));

const ContactFab = lazy(() => import("@/components/ContactFab"));
const AIChatFab = lazy(() => import("@/components/AIChatFab"));
const SEOHead = lazy(() => import("@/components/SEOHead"));
const MobileLanguagePicker = lazy(() => import("@/components/MobileLanguagePicker"));

const BelowFoldFallback = () => (
  <div className="bg-background py-12 text-center" style={{ minHeight: "400px" }}>
    <div className="animate-pulse h-8 w-48 bg-muted rounded mx-auto mt-8" />
  </div>
);

const CategoriesFallback = () => <div className="bg-background" style={{ minHeight: "72px" }} aria-hidden="true" />;

// Eager prefetch + warm sessionStorage from localStorage so FeaturedSection renders instantly.
const PREFETCH_KEY = "biletime-featured-sections-v6";
const PREFETCH_TS = "biletime-featured-sections-v6-ts";
const PREFETCH_TTL = 24 * 60 * 60 * 1000;
if (typeof window !== "undefined") {
  try {
    if (!sessionStorage.getItem(PREFETCH_KEY)) {
      const lc = localStorage.getItem(PREFETCH_KEY);
      const ts = Number(localStorage.getItem(PREFETCH_TS) || 0);
      if (lc && Date.now() - ts < PREFETCH_TTL) {
        sessionStorage.setItem(PREFETCH_KEY, lc);
      }
    }
  } catch {}

  supabase.rpc("get_homepage_sections_with_events").then(({ data, error }) => {
    if (error || !data) return;
    try {
      const rows = (data as unknown as Array<{ id: string; title: string; slug: string; card_template: string; events: any[] }>) || [];
      const result = rows.map((sec) => ({
        id: sec.id,
        title: sec.title,
        slug: sec.slug,
        card_template: sec.card_template || "classic-hero-split",
        events: sec.events ?? [],
      }));
      const json = JSON.stringify(result);
      sessionStorage.setItem(PREFETCH_KEY, json);
      localStorage.setItem(PREFETCH_KEY, json);
      localStorage.setItem(PREFETCH_TS, String(Date.now()));
    } catch {
      // ignore
    }
  });
}

const Index = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user } = useAuth();

  const params = new URLSearchParams(search);
  const paymentError = params.get("payment_status") === "error";
  const paymentSuccess = params.get("payment_status") === "success";
  const errorMessage = params.get("message");
  const paymentProvider = params.get("provider");
  const successOrderId = params.get("orderId");

  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    const section = params.get("section");
    if (section) {
      requestAnimationFrame(() => {
        document.getElementById(section)?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }

    if (paymentError && errorMessage) {
      toast({ title: "Ödeme Başarısız", description: errorMessage || "Bilinmeyen Hata", variant: "destructive", duration: 10000 });
      console.error("Payment failed from callback:", errorMessage);
    }
    
    if (paymentSuccess) {
      setShowSuccess(true);
    }
  }, [search, navigate, paymentError, errorMessage, paymentSuccess]);

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background pb-14 md:pb-0">
      <Suspense fallback={null}>
        <SEOHead path="/" />
      </Suspense>
      {isMobile && (
        <Suspense fallback={null}>
          <MobileLanguagePicker />
        </Suspense>
      )}
      {!isMobile && (
        <Suspense fallback={null}>
          <Navbar />
        </Suspense>
      )}
      
      {/* 🔴 PAYMENT ERROR PERSISTENT ALERT FOR DEBUGGING */}
      {paymentError && (
        <div className="bg-destructive text-destructive-foreground p-4 text-center sticky top-0 z-50 shadow-lg border-b border-destructive-foreground/20">
          <h3 className="font-bold text-lg mb-1">Ödeme Reddedildi (Garanti)</h3>
          <p className="font-mono text-sm break-all">{decodeURIComponent(errorMessage || '')}</p>
          {paymentProvider && <p className="text-xs opacity-80 mt-1">Provider: {paymentProvider}</p>}
          <button onClick={() => navigate('/')} className="mt-2 text-xs underline opacity-80 hover:opacity-100">Kapat</button>
        </div>
      )}

      {/* 🎉 PAYMENT SUCCESS MODAL */}
      <Dialog open={showSuccess} onOpenChange={setShowSuccess}>
        <DialogContent className="sm:max-w-md text-center p-8 flex flex-col items-center justify-center border-2 border-primary/20" onCloseAutoFocus={handleCloseSuccess}>
          <div className="h-20 w-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4 shadow-inner ring-4 ring-green-50 dark:ring-green-900/10">
            <CheckCircle2 className="h-10 w-10 text-green-600 dark:text-green-400" />
          </div>
          <DialogHeader className="flex flex-col items-center w-full">
            <DialogTitle className="text-2xl font-bold text-foreground">Teşekkürler!</DialogTitle>
            <DialogDescription className="text-center text-base mt-2 text-muted-foreground w-full">
              Biletiniz başarıyla onaylandı ve <strong className="text-foreground">e-posta adresinize</strong> gönderildi.
            </DialogDescription>
          </DialogHeader>
          {user ? (
            <Button onClick={() => { setShowSuccess(false); navigate("/#profile"); }} className="mt-6 w-full py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all">
              Biletlerimi Görüntüle
            </Button>
          ) : (
            <div className="w-full bg-amber-50 dark:bg-amber-950/20 rounded-lg p-4 my-4 flex flex-col gap-2 items-center justify-center border border-amber-200 dark:border-amber-800">
              <UserPlus className="h-6 w-6 text-amber-600 dark:text-amber-400 mb-1" />
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Biletlerinizi Takip Edin</p>
              <p className="text-xs text-amber-700/80 dark:text-amber-400/80">Kayıt olarak aldığınız tüm biletleri kolayca tek bir ekrandan takip edebilir ve QR kodlarına ulaşabilirsiniz.</p>
            </div>
          )}
          <DialogFooter className="w-full sm:justify-center flex-col sm:flex-row gap-2 mt-2">
            <Button variant="outline" onClick={handleCloseSuccess} className="w-full sm:w-auto">
              Ana Sayfaya Dön
            </Button>
            {user ? (
              <Button onClick={() => navigate("/my-tickets", { replace: true })} className="w-full sm:w-auto group">
                Biletlerim'e Git
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            ) : (
              <Button onClick={() => navigate("/auth?redirect=/my-tickets", { replace: true })} className="w-full sm:w-auto group">
                Google ile Giriş Yap / Kayıt Ol
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <main id="main-content">
        <HeroSection />

        <Suspense fallback={<CategoriesFallback />}>
          <Categories />
        </Suspense>

        <Suspense fallback={<BelowFoldFallback />}>
          <FeaturedSections />
        </Suspense>

        <Suspense fallback={null}>
          <HomeSections />
        </Suspense>

        <Suspense fallback={null}>
          <BestSellers />
        </Suspense>

        <Suspense fallback={null}>
          <PopularActivities />
        </Suspense>
      </main>

      <Suspense fallback={null}>
        <GroupTicketBanner />
        <Footer />
        <ContactFab hideFab />
        <AIChatFab />
      </Suspense>
    </div>
  );
};

export default Index;
